import { useState } from "react";
import type { Account, Owner } from "../../types";
import { shortName, todayIn } from "../../lib/finance";
import { useProfileNames } from "../../components/ProfilesContext";
import DateInput from "../../components/DateInput";
import FormActions from "../../components/FormActions";
import { api } from "../../lib/api";

export type AccountDraft = {
  id?: string;
  expected?: Account;
  name: string;
  owner: Owner;
  type: string;
  currency: string;
  openingBalance: string;
  creditLimit: string;
  statementDay: string;
  dueDay: string;
  statementRecordedDate: string;
  statementBalance: string;
  minimumPayment: string;
  statementDueDate: string;
  notes: string;
  active: boolean;
};

export const blankAccountDraft = (owner: Owner): AccountDraft => ({
  name: "",
  owner,
  type: "Bank",
  currency: "MVR",
  openingBalance: "0",
  creditLimit: "",
  statementDay: "",
  dueDay: "",
  statementRecordedDate: "",
  statementBalance: "",
  minimumPayment: "",
  statementDueDate: "",
  notes: "",
  active: true,
});

export const fromAccountToDraft = (a: Account): AccountDraft => ({
  id: a.id,
  expected: a,
  name: shortName(a.name),
  owner: a.owner,
  type: a.type,
  currency: a.currency,
  openingBalance: String(a.openingBalance ?? a.balance),
  creditLimit: a.creditLimit == null ? "" : String(a.creditLimit),
  statementDay: a.statementDay == null ? "" : String(a.statementDay),
  dueDay: a.dueDay == null ? "" : String(a.dueDay),
  statementRecordedDate: a.statementRecordedDate || "",
  statementBalance: a.statementBalance == null ? "" : String(a.statementBalance),
  minimumPayment: a.minimumPayment == null ? "" : String(a.minimumPayment),
  statementDueDate: a.statementDueDate || "",
  notes: a.notes || "",
  active: a.active !== false,
});

export interface AccountFormProps {
  initialAccount?: Account | null;
  defaultOwner: Owner;
  types: string[];
  currencies: string[];
  isUsed: (id: string) => boolean;
  onCancel: () => void;
  onSaved: () => Promise<unknown>;
}

export default function AccountForm({
  initialAccount,
  defaultOwner,
  types,
  currencies,
  isUsed,
  onCancel: _onCancel,
  onSaved,
}: AccountFormProps) {
  const [draft, setDraft] = useState<AccountDraft>(() =>
    initialAccount ? fromAccountToDraft(initialAccount) : blankAccountDraft(defaultOwner)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const profileNames = useProfileNames();

  async function save() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const statementChanged =
        draft.type === "Credit Card" &&
        (!draft.expected ||
          draft.statementBalance !== String(draft.expected.statementBalance ?? "") ||
          draft.minimumPayment !== String(draft.expected.minimumPayment ?? "") ||
          draft.statementDueDate !== (draft.expected.statementDueDate || ""));

      await api("settings/accounts/save", {
        ...draft,
        statementRecordedDate: statementChanged ? todayIn() : draft.statementRecordedDate,
      });
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const isLocked = !!draft.id && isUsed(draft.id);

  return (
    <form
      className="account-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {error && <div className="inline-error" role="alert">{error}</div>}

      <label>
        Account name
        <input
          required
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="e.g. Primary account"
        />
      </label>

      <div className="account-editor-grid">
        <label>
          Profile
          <select
            value={draft.owner}
            disabled={isLocked}
            onChange={(e) => setDraft({ ...draft, owner: e.target.value as Owner })}
          >
            {profileNames.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>

        <label>
          Type
          <select
            value={draft.type}
            disabled={isLocked}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
          >
            {types.map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>

        <label>
          Currency
          <select
            value={draft.currency}
            disabled={isLocked}
            onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
          >
            {currencies.map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>

        <label>
          Opening balance
          <input
            required
            inputMode="decimal"
            value={draft.openingBalance}
            onChange={(e) => setDraft({ ...draft, openingBalance: e.target.value })}
          />
        </label>
      </div>

      {draft.type === "Credit Card" && (
        <>
          <div className="account-editor-grid">
            <label>
              Credit limit
              <input
                required
                inputMode="decimal"
                value={draft.creditLimit}
                onChange={(e) => setDraft({ ...draft, creditLimit: e.target.value })}
              />
            </label>
            <label>
              Statement due date
              <DateInput
                value={draft.statementDueDate}
                onChange={(e) => setDraft({ ...draft, statementDueDate: e.target.value })}
              />
            </label>
            <label>
              Statement balance
              <input
                inputMode="decimal"
                value={draft.statementBalance}
                onChange={(e) => setDraft({ ...draft, statementBalance: e.target.value })}
              />
            </label>
            <label>
              Minimum payment
              <input
                inputMode="decimal"
                value={draft.minimumPayment}
                onChange={(e) => setDraft({ ...draft, minimumPayment: e.target.value })}
              />
            </label>
          </div>
          <p className="settings-note">
            Statement values come from the bank. Saving them records today as the tracking start date.
          </p>
        </>
      )}

      <label>
        Notes
        <textarea
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>

      {!draft.active && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          />
          Reactivate this account
        </label>
      )}

      {isLocked && (
        <p className="settings-note">
          Profile, type and currency are locked because this account has transaction history.
        </p>
      )}

      <FormActions label="Save account" busy={busy} />
    </form>
  );
}
